import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { format } from "date-fns";
import { OrganizationMemberClientAccessSource } from "@common/types/organizationMemberClientAccess";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useOrganizationDemoWorkspace } from "@hooks/useOrganizationDemoWorkspace";
import {
    useOrganizationUserCache,
    ORGANIZATION_USER_CACHE_KEYS,
} from "@hooks/useOrganizationUserCache";
import { Button } from "@design-system/Button/Button";
import { useConstant } from "@hooks/useConstant";
import { VStack } from "@design-system/VStack/VStack";
import { CloseIcon } from "@icons";
import styles from "@design-system/Banner/OverPageCTA.module.scss";
import localStyles from "./AccountantsTrainingCTA.module.scss";

const ACCOUNTANTS_TRAINING_URL =
    "https://watch.getcontrast.io/sessions/kick-kick-for-accountants-weekly-training";
const ACCOUNTANTS_DOCS_URL = "https://docs.kick.co/accountants";

const dismissedDatesSchema = z.array(z.string());

/**
 * Getting started banner on the Clients tab.
 * Shows only within the first week from onboardingCompletedAt.
 * Can be dismissed for the day, but will reappear the next day.
 */
interface AccountantsTrainingCTAProps {
    onVisibilityChange?: (isVisible: boolean) => void;
}

export const AccountantsTrainingCTA = ({
    onVisibilityChange,
}: AccountantsTrainingCTAProps) => {
    const activeOrganization = useActiveOrganization();
    const { demoWorkspace, openDemoWorkspace } = useOrganizationDemoWorkspace();
    const [dismissedDates, setDismissedDates] = useOrganizationUserCache(
        ORGANIZATION_USER_CACHE_KEYS.ACCOUNTANTS_TRAINING_CTA_DISMISSED_DATES,
        dismissedDatesSchema,
    );

    const today = useConstant(() => new Date());
    const todayDateString = format(today, "yyyy-MM-dd");

    const [isDismissed, setIsDismissed] = useState(
        dismissedDates?.includes(todayDateString) ?? false,
    );

    const onboardingCompletedAt = activeOrganization?.onboardingCompletedAt;

    const isWithinFirstWeek = useMemo(() => {
        if (!onboardingCompletedAt) {
            return false;
        }

        const onboardingDate = new Date(onboardingCompletedAt);

        // Reset time to midnight for accurate date comparison
        const onboardingDateOnly = new Date(
            onboardingDate.getFullYear(),
            onboardingDate.getMonth(),
            onboardingDate.getDate(),
        );
        const todayDateOnly = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
        );

        const daysSinceOnboarding = Math.floor(
            (todayDateOnly.getTime() - onboardingDateOnly.getTime()) /
                (1000 * 60 * 60 * 24),
        );

        return daysSinceOnboarding >= 0 && daysSinceOnboarding < 7;
    }, [onboardingCompletedAt, today]);

    const handleDismiss = useCallback(() => {
        const newDismissedDates = [
            ...(dismissedDates ?? []),
            format(new Date(), "yyyy-MM-dd"),
        ];
        setDismissedDates(newDismissedDates);
        setIsDismissed(true);
    }, [dismissedDates, setDismissedDates]);

    const exploreDemo = useCallback(() => {
        openDemoWorkspace(OrganizationMemberClientAccessSource.CLIENTS_LIST);
    }, [openDemoWorkspace]);

    const isVisible = isWithinFirstWeek && !isDismissed;

    useEffect(() => {
        onVisibilityChange?.(isVisible);
    }, [isVisible, onVisibilityChange]);

    if (!isVisible) {
        return null;
    }

    // Render OverPageCTA without dismissKey and add our own dismiss button
    // to handle organization-scoped cache instead of workspace-scoped cache
    return (
        <div
            className={classNames(
                styles.overPageCtaContainer,
                localStyles.banner,
            )}
        >
            <div className={classNames(styles.leftContent, localStyles.copy)}>
                <VStack gap={4}>
                    <div className={styles.title}>Getting started</div>
                    <div className={styles.subtitle}>
                        Register for our weekly live accountant training
                        {demoWorkspace ? ", " : " or "}check out our help docs
                        {demoWorkspace ? ", or explore a demo client." : "."}
                    </div>
                </VStack>
            </div>
            <div
                className={classNames(styles.rightContent, localStyles.actions)}
            >
                <Button asChild variant="primary" size="md">
                    <a
                        href={ACCOUNTANTS_TRAINING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Register
                    </a>
                </Button>
                <Button asChild variant="primary" size="md">
                    <a
                        href={ACCOUNTANTS_DOCS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Open docs
                    </a>
                </Button>
                {demoWorkspace && (
                    <Button variant="primary" size="md" onClick={exploreDemo}>
                        Explore demo
                    </Button>
                )}
                <Button
                    iconOnly
                    onClick={handleDismiss}
                    variant="secondary"
                    size="sm"
                    aria-label="Dismiss training banner"
                >
                    <CloseIcon />
                </Button>
            </div>
        </div>
    );
};
